import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { type Event } from 'nostr-tools';
import {
  Heart, User, ArrowRight, RefreshCw, Sprout, Film, Music,
  FileText, Target, PenSquare, X, Sparkles,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useFollows } from '../hooks/useFollows';
import {
  GOAL_KIND, GOAL_TAG, GOAL_PLEDGE_KIND, GOAL_PLEDGE_TAG,
  currentMonth,
} from '../lib/creatorSupport';

// ── Kinds & Tags ────────────────────────────────────────────────────
const TIP_RECEIPT_KIND = 9735;
const MEMBERSHIP_SUB_TAG = 'bch-membership-sub';
const VIDEO_KINDS = [22, 21, 34236, 34235];
const ARTICLE_KIND = 30023;
const RADIO_TRACK_KIND = 1808;
const RADIO_TRACK_TAG = 'bch-radio-track';

// ── localStorage tracking ───────────────────────────────────────────
const DISCOVERED_KEY = 'bchnostr:discovered-creators';
const DISMISSED_KEY = 'bchnostr:discover-dismissed';

function getDiscoveredSet(): Set<string> {
  try {
    const raw = localStorage.getItem(DISCOVERED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

function markDiscovered(pubkey: string) {
  const set = getDiscoveredSet();
  set.add(pubkey);
  try { localStorage.setItem(DISCOVERED_KEY, JSON.stringify([...set])); } catch {}
}

function isDismissed(): boolean {
  try {
    const ts = localStorage.getItem(DISMISSED_KEY);
    if (!ts) return false;
    // Dismissed for 24 hours
    return Date.now() - parseInt(ts, 10) < 24 * 3600 * 1000;
  } catch { return false; }
}

function setDismissed() {
  try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch {}
}

// ── Content type detection ──────────────────────────────────────────
type CreatorType = 'Writer' | 'Musician' | 'Video Creator' | 'Community Builder' | 'Creator';

interface CandidateInfo {
  pubkey: string;
  score: number;
  contentType: CreatorType;
  hasGoal: boolean;
}

// ── Discovery Toast ─────────────────────────────────────────────────
function DiscoveryToast({ creatorPubkey, onDismiss }: { creatorPubkey: string; onDismiss: () => void }) {
  const { profile } = useNostrProfile(creatorPubkey);
  const name = profile?.display_name || profile?.name || 'a new creator';

  useEffect(() => {
    const t = setTimeout(onDismiss, 6000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] animate-slide-down max-w-sm w-full px-4">
      <div className="relative flex items-start gap-3 px-4 py-3.5 rounded-2xl bg-emerald-500 text-white shadow-2xl shadow-emerald-500/30 border border-emerald-400/30">
        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
          <span className="text-xl">🌱</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold">You discovered and supported {name}!</p>
          <p className="text-xs text-white/80 mt-0.5">Thanks for helping grow the BCH creator economy.</p>
        </div>
        <button onClick={onDismiss} className="p-1 rounded-lg hover:bg-white/20 transition-colors shrink-0 mt-0.5">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ── Content Type Badge ──────────────────────────────────────────────
function ContentTypeBadge({ type }: { type: CreatorType }) {
  const config: Record<CreatorType, { icon: React.ElementType; color: string; bg: string }> = {
    'Writer':            { icon: PenSquare, color: '#8B5CF6', bg: '#8B5CF620' },
    'Musician':          { icon: Music,     color: '#EF4444', bg: '#EF444420' },
    'Video Creator':     { icon: Film,      color: '#F59E0B', bg: '#F59E0B20' },
    'Community Builder':{ icon: Heart,     color: '#10B981', bg: '#10B98120' },
    'Creator':           { icon: Sparkles,  color: '#6366F1', bg: '#6366F120' },
  };
  const { icon: Icon, color, bg } = config[type];
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ color, backgroundColor: bg }}>
      <Icon className="w-3 h-3" /> {type}
    </span>
  );
}

// ── Suggestion Card ─────────────────────────────────────────────────
function SuggestionCard({ pubkey, contentType, hasGoal, onNext }: {
  pubkey: string;
  contentType: CreatorType;
  hasGoal: boolean;
  onNext: () => void;
}) {
  const { profile } = useNostrProfile(pubkey);
  const name = profile?.display_name || profile?.name || pubkey.slice(0, 12) + '…';
  const bio = profile?.about?.slice(0, 100) || null;

  return (
    <div className="space-y-2.5">
      <Link
        to={`/profile/${pubkey}`}
        className="flex items-center gap-3 rounded-2xl border border-emerald-500/20 bg-gradient-to-r from-emerald-500/5 via-transparent to-primary/5 p-3.5 hover:border-emerald-500/40 transition-all group"
      >
        {/* Avatar */}
        <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 border border-border bg-muted">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <User className="w-5 h-5 text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate group-hover:text-primary transition-colors">{name}</p>
          {bio && <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{bio}</p>}
          <div className="flex items-center gap-1.5 mt-1">
            <ContentTypeBadge type={contentType} />
            {hasGoal && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold text-primary bg-primary/10">
                <Target className="w-3 h-3" /> Active Goal
              </span>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="flex items-center gap-1 px-3 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold shrink-0 group-hover:bg-emerald-600 transition-colors shadow-sm">
          <span>💚</span> Support
        </div>
      </Link>

      {/* Discover another */}
      <button
        onClick={onNext}
        className="flex items-center justify-center gap-1.5 w-full py-2 rounded-xl border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
      >
        <RefreshCw className="w-3 h-3" /> Discover Another Creator
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// ██  SUPPORT SOMEONE NEW — Shared component
// ══════════════════════════════════════════════════════════════════════

interface SupportSomeoneNewProps {
  /** Compact mode hides the outer section header (for embedding inline) */
  compact?: boolean;
  /** Whether the card can be dismissed */
  dismissable?: boolean;
}

export default function SupportSomeoneNew({ compact = false, dismissable = false }: SupportSomeoneNewProps) {
  const { user } = useAppContext();
  const [dismissed, setDismissedState] = useState(() => dismissable && isDismissed());

  if (!user || dismissed) return null;
  return (
    <SupportSomeoneNewInner
      pubkey={user.pubkey}
      compact={compact}
      dismissable={dismissable}
      onDismiss={() => { setDismissed(); setDismissedState(true); }}
    />
  );
}

function SupportSomeoneNewInner({ pubkey, compact, dismissable, onDismiss }: {
  pubkey: string;
  compact: boolean;
  dismissable: boolean;
  onDismiss: () => void;
}) {
  const { follows } = useFollows(pubkey);
  const followSet = useMemo(() => new Set(follows), [follows]);

  // ── Query user's support history ──────────────────────────────────
  const allTimeSince = useMemo(() => Math.floor(Date.now() / 1000) - 365 * 86400, []);

  const { events: tipsSent } = useNostrQuery({
    filters: [{ kinds: [TIP_RECEIPT_KIND], authors: [pubkey], '#t': ['bch-tip'], limit: 500, since: allTimeSince }],
    allRelays: true,
  });

  const { events: membershipPayments } = useNostrQuery({
    filters: [{ kinds: [TIP_RECEIPT_KIND], authors: [pubkey], '#t': [MEMBERSHIP_SUB_TAG], limit: 200, since: allTimeSince }],
    allRelays: true,
  });

  const { events: goalPledges } = useNostrQuery({
    filters: [{ kinds: [GOAL_PLEDGE_KIND], authors: [pubkey], '#t': [GOAL_PLEDGE_TAG], limit: 200, since: allTimeSince }],
    allRelays: true,
  });

  // Build set of already-supported creators
  const supportedPubkeys = useMemo(() => {
    const pks = new Set<string>();
    for (const ev of tipsSent) {
      const p = ev.tags?.find((t: string[]) => t[0] === 'p')?.[1];
      if (p && p !== pubkey) pks.add(p);
    }
    for (const ev of membershipPayments) {
      const p = ev.tags?.find((t: string[]) => t[0] === 'p')?.[1];
      if (p && p !== pubkey) pks.add(p);
    }
    for (const ev of goalPledges) {
      const p = ev.tags?.find((t: string[]) => t[0] === 'p')?.[1];
      if (p && p !== pubkey) pks.add(p);
    }
    return pks;
  }, [tipsSent, membershipPayments, goalPledges, pubkey]);

  // ── Query active content to find candidates ───────────────────────
  const [since14d] = useState(() => Math.floor(Date.now() / 1000) - 14 * 86400);

  // Posts — active BCH Nostr members plus anyone posting with BCH hashtags
  // (posts no longer require a BCH hashtag to appear in the community feed).
  const { events: recentPosts } = useNostrQuery({
    filters: [
      { kinds: [1], '#t': ['bchnostr'], limit: 300, since: since14d },
      { kinds: [1], '#t': ['bch'], limit: 300, since: since14d },
      { kinds: [1], '#t': ['bitcoincash'], limit: 200, since: since14d },
    ],
    allRelays: true,
  });

  // Clips
  const { events: recentClips } = useNostrQuery({
    filters: [{ kinds: VIDEO_KINDS, '#t': ['bch', 'bitcoincash'], limit: 100, since: since14d }],
    allRelays: true,
    extraRelays: ['wss://relay.divine.video'],
  });

  // Radio tracks
  const { events: recentTracks } = useNostrQuery({
    filters: [{ kinds: [RADIO_TRACK_KIND], '#t': [RADIO_TRACK_TAG], limit: 100, since: since14d }],
    allRelays: true,
  });

  // Articles
  const { events: recentArticles } = useNostrQuery({
    filters: [{ kinds: [ARTICLE_KIND], '#t': ['bch'], limit: 50, since: since14d }],
    allRelays: true,
  });

  // Active creator goals
  const { events: goalEvents } = useNostrQuery({
    filters: [{ kinds: [GOAL_KIND], '#t': [GOAL_TAG], limit: 100 }],
    allRelays: true,
  });

  const activeGoalPubkeys = useMemo(() => {
    const pks = new Set<string>();
    for (const ev of goalEvents) {
      try {
        const data = JSON.parse(ev.content);
        if (data.active) pks.add(ev.pubkey);
      } catch {}
    }
    return pks;
  }, [goalEvents]);

  // ── Build ranked candidate list ───────────────────────────────────
  const candidates = useMemo((): CandidateInfo[] => {
    // Count activity by pubkey and detect content types
    const activity = new Map<string, { posts: number; clips: number; tracks: number; articles: number }>();

    const bump = (pk: string, type: 'posts' | 'clips' | 'tracks' | 'articles') => {
      if (pk === pubkey) return;
      if (supportedPubkeys.has(pk)) return;
      if (followSet.has(pk)) return; // exclude already-followed
      const entry = activity.get(pk) || { posts: 0, clips: 0, tracks: 0, articles: 0 };
      entry[type]++;
      activity.set(pk, entry);
    };

    for (const ev of recentPosts) bump(ev.pubkey, 'posts');
    for (const ev of recentClips) bump(ev.pubkey, 'clips');
    for (const ev of recentTracks) bump(ev.pubkey, 'tracks');
    for (const ev of recentArticles) bump(ev.pubkey, 'articles');

    return [...activity.entries()].map(([pk, counts]) => {
      // Weighted score — clips/tracks/articles count more (rarer content = more effort)
      const score = counts.posts + counts.clips * 3 + counts.tracks * 4 + counts.articles * 3
        + (activeGoalPubkeys.has(pk) ? 5 : 0);

      // Determine primary content type
      let contentType: CreatorType = 'Creator';
      const maxType = Math.max(counts.clips, counts.tracks, counts.articles, counts.posts);
      if (maxType === 0) contentType = 'Creator';
      else if (counts.tracks === maxType) contentType = 'Musician';
      else if (counts.clips === maxType) contentType = 'Video Creator';
      else if (counts.articles === maxType) contentType = 'Writer';
      else if (counts.posts >= 5) contentType = 'Community Builder';

      return {
        pubkey: pk,
        score,
        contentType,
        hasGoal: activeGoalPubkeys.has(pk),
      };
    }).filter(c => c.score > 0)
      .sort((a, b) => b.score - a.score);
  }, [recentPosts, recentClips, recentTracks, recentArticles, activeGoalPubkeys, supportedPubkeys, followSet, pubkey]);

  // ── Cycle through suggestions ─────────────────────────────────────
  const [index, setIndex] = useState(0);

  // Pick from the top candidates with some randomness
  const currentCandidate = useMemo(() => {
    if (candidates.length === 0) return null;
    // Use top 15 for the pool
    const pool = candidates.slice(0, 15);
    const i = index % pool.length;
    return pool[i];
  }, [candidates, index]);

  const handleNext = useCallback(() => {
    setIndex(prev => prev + 1);
  }, []);

  // ── Discovery toast ───────────────────────────────────────────────
  const [discoveryToast, setDiscoveryToast] = useState<string | null>(null);
  const prevSupportedRef = useRef(new Set<string>());

  useEffect(() => {
    if (supportedPubkeys.size === 0) return;
    const prev = prevSupportedRef.current;
    if (prev.size === 0) {
      prevSupportedRef.current = new Set(supportedPubkeys);
      return;
    }
    for (const pk of supportedPubkeys) {
      if (!prev.has(pk)) {
        const discovered = getDiscoveredSet();
        if (!discovered.has(pk)) {
          markDiscovered(pk);
          setDiscoveryToast(pk);
        }
      }
    }
    prevSupportedRef.current = new Set(supportedPubkeys);
  }, [supportedPubkeys]);

  // ── Render ────────────────────────────────────────────────────────
  if (!currentCandidate) return null; // No suggestions available

  const card = (
    <SuggestionCard
      pubkey={currentCandidate.pubkey}
      contentType={currentCandidate.contentType}
      hasGoal={currentCandidate.hasGoal}
      onNext={handleNext}
    />
  );

  if (compact) {
    return (
      <>
        {discoveryToast && <DiscoveryToast creatorPubkey={discoveryToast} onDismiss={() => setDiscoveryToast(null)} />}
        <div className="mx-3 my-2">
          <div className="rounded-2xl border border-emerald-500/20 overflow-hidden">
            <div className="flex items-center justify-between px-4 pt-3.5 pb-1">
              <p className="text-xs font-bold flex items-center gap-1.5">
                <span className="text-sm">💚</span> Support Someone New
              </p>
              {dismissable && (
                <button onClick={onDismiss} className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Dismiss for 24h">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="px-3.5 pb-3.5">
              {card}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {discoveryToast && <DiscoveryToast creatorPubkey={discoveryToast} onDismiss={() => setDiscoveryToast(null)} />}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Sprout className="w-3.5 h-3.5" /> Support Someone New
          </p>
          {dismissable && (
            <button onClick={onDismiss} className="text-[10px] text-muted-foreground hover:text-foreground transition-colors">
              Dismiss
            </button>
          )}
        </div>
        {card}
      </div>
    </>
  );
}

